import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { GameState, Investigator, UserAction } from '../api';
import { GameDialog } from './GameDialog';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { orderedGameInvestigators } from './investigatorOrder';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { jsonEqual } from '../utils/jsonEqual';
import { InvestigatorNavigation } from './InvestigatorNavigation';
import { isUndoAction } from './UpkeepActions';
import { useInspectorScroll } from './useInspectorScroll';

// Polling replaces DTO objects even when the displayed data has not changed.
const InspectorSheetContent = memo(InvestigatorDisplay, jsonEqual);

/** Build offscreen digital inventories as they approach the scroll viewport. */
const DeferredInspectorSheet: React.FC<React.ComponentProps<typeof InspectorSheetContent> & { eager: boolean }> = ({ eager, ...props }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(eager || typeof IntersectionObserver === 'undefined');
  if (eager && !ready) setReady(true);
  useEffect(() => {
    if (ready || eager) return;
    const node = ref.current!;
    const reveal = () => setReady(true);
    const region = node.closest('section')!;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) reveal();
    }, { root: node.closest('.game-dialog-body'), rootMargin: '300px 0px' });
    observer.observe(node);
    region.addEventListener('focus', reveal);
    return () => { observer.disconnect(); region.removeEventListener('focus', reveal); };
  }, [ready, eager]);
  return ready || eager ? <InspectorSheetContent {...props} /> : <div ref={ref} style={{ minHeight: 1000 }} aria-busy="true" />;
};

export const InvestigatorInspector: React.FC<{
  state?: GameState; investigator: Investigator; investigators?: Investigator[];
  viewerName?: string; selectedInvestigatorName?: string;
  variant?: 'reference' | 'game';
  actions?: UserAction[]; loading?: boolean; language?: string; onExecuteAction?: (id: string) => void;
  onClose: () => void;
  open?: boolean;
  dockTargetRef?: React.RefObject<HTMLButtonElement | null>;
}> = ({ state, investigator, investigators = [investigator], viewerName, selectedInvestigatorName, variant = 'game', actions = [], loading, language, onExecuteAction, onClose, open = true, dockTargetRef }) => {
  const digital = useMonsterTokenStyle() === 'Digital';
  const [navigation, setNavigation] = useState({ name: investigator.name, smooth: false, sequence: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    setMenuOpen(false);
    if (open) setNavigation({ name: investigator.name, smooth: false, sequence: navigation.sequence + 1 });
  }
  const sheetsRef = useInspectorScroll(open, navigation);
  const closeMenu = () => {
    setMenuOpen(false);
    menuTriggerRef.current?.focus({ preventScroll: true });
  };
  const navigate = (name: string) => {
    setNavigation(previous => ({ name, smooth: true, sequence: previous.sequence + 1 }));
    if (menuOpen) closeMenu();
  };
  const execute = useCallback((id: string) => {
    if (!loading) onExecuteAction?.(id);
  }, [loading, onExecuteAction]);
  const actingNames = actions.filter(action => !action.disabled && !isUndoAction(action) && action.cardType !== 'Investigator')
    .flatMap(action => action.investigatorName ? [action.investigatorName] : []);
  const all = variant === 'reference' ? [investigator] : orderedGameInvestigators(state, [...investigators, investigator]);
  const same = (left?: string, right?: string) => !!left && !!right && left.toLowerCase() === right.toLowerCase();
  const selected = all.find(value => same(value.name, selectedInvestigatorName));
  let priority: Investigator | undefined;
  if (state?.expectedPlayersCount === 1) {
    const actionable = all.filter(value => actions.some(action => action.cardType !== 'Investigator' && same(action.investigatorName, value.name)));
    priority = actionable.find(value => same(value.name, selected?.name)) ?? actionable[0];
  } else {
    const owner = state?.players?.find(player => same(player.name, viewerName));
    const controlled = owner?.controlledInvestigators ?? [];
    priority = selected && controlled.some(value => same(value, selected.name)) ? selected :
      controlled.map(value => all.find(candidate => same(candidate.name, value))).find(Boolean);
  }
  const firstIndex = all.findIndex(value => same(value.name, priority?.name ?? investigator.name));
  const ordered = firstIndex > 0 ? [...all.slice(firstIndex), ...all.slice(0, firstIndex)] : all;
  const first = state?.firstInvestigator
    ? all.find(value => same(value.name, state.firstInvestigator ?? undefined))
    : all.find(value => state?.players?.some(player => same(player.name, state.firstPlayer ?? undefined)
      && player.controlledInvestigators?.some(name => same(name, value.name))));
  return <GameDialog variant="investigator" title={variant === 'reference' ? investigator.name : 'Investigators'} open={open} dockTargetRef={dockTargetRef} onClose={onClose}
    onEscape={menuOpen ? closeMenu : onClose} header={variant !== 'reference' &&
      <InvestigatorNavigation investigators={ordered} state={state} firstName={first?.name} currentName={navigation.name} actingNames={actingNames}
        open={menuOpen} onOpenChange={setMenuOpen} triggerRef={menuTriggerRef} onNavigate={navigate} />
    }>
    <div ref={sheetsRef} className="game-inspector-sheets">
      {ordered.map((value, index) => {
        const location = state?.locations?.find(place => place.investigatorNames?.some(candidate => same(candidate, value.name)))?.name;
        const ownActions = actions.filter(action => same(action.investigatorName, value.name))
          .map(action => ({ ...action, investigatorName: value.name, disabled: !!loading || !!action.disabled }));
        const owner = state?.players?.find(player =>
          player.controlledInvestigators?.some(candidate => same(candidate, value.name)));
        const isFirst = state?.firstInvestigator
          ? same(value.name, state.firstInvestigator)
          : same(owner?.name, state?.firstPlayer ?? undefined);
        const firstPlayerBadge = isFirst && <span className="first-player-badge">First Player</span>;
        const isOnline = !!state?.playersDetailed?.find(player => same(player.name, owner?.name))?.isOnline;
        const playerInfo = state && state.expectedPlayersCount > 1 && owner && <span className="game-inspector-player">
          <span className="owner-badge">({owner.name})</span>
          <span className="online-status" role="img" aria-label={`${owner.name}: ${isOnline ? 'Online' : 'Offline'}`}
            title={`${owner.name}: ${isOnline ? 'Online' : 'Offline'}`} style={{ background: isOnline ? 'green' : 'red' }} />
        </span>;
        return <div key={value.name} className="game-inspector-sheet" data-investigator={value.name}>
          {!digital && variant !== 'reference' && <h3 className="game-inspector-sheet-heading"><span>{value.name}</span>{firstPlayerBadge}{playerInfo}</h3>}
          <section className={`game-investigator-sheet-scroll${digital ? ' game-investigator-sheet-digital' : ''}`} tabIndex={0} aria-label={`${value.name} investigator sheet`}
            onKeyDown={event => {
              if (event.target === event.currentTarget && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
                event.preventDefault();
                event.currentTarget.scrollBy({ left: event.key === 'ArrowRight' ? 60 : -60 });
              }
            }}>
            <DeferredInspectorSheet eager={!digital || index === 0 || same(value.name, navigation.name)} variant={variant} gameState={state} investigator={value} currentLocationName={location ?? (digital ? undefined : value.startingLocation)} showStats
              deckInfo={{ commonItems: state?.decks?.commonItems, uniqueItems: state?.decks?.uniqueItems, spells: state?.decks?.spells }}
              language={language} availableActions={ownActions}
              onExecuteAction={onExecuteAction ? execute : undefined} />
          </section>
        </div>;
      })}
    </div>
  </GameDialog>;
};
